import type { MechanicDefinition } from "./types";

export const povSwitch: MechanicDefinition = {
  id: "pov_switch",
  disabled: true,
  name: "Switch POV",
  description: "Switch the active point-of-view character. Supports multiplayer hand-offs and single-player multi-POV storytelling with cinematic transitions.",
  icon: "RefreshCw",
  category: "systems",
  auditPasses: 0,

  aiHints: {
    purpose: "Lets the narrator or player switch the active POV character for multi-perspective storytelling or multiplayer hand-offs.",
    whenToUse: "Multiplayer adventures, ensemble-cast stories, detective/mystery narratives with multiple investigators, or any story benefiting from multiple perspectives.",
    synergies: ["turn_structure", "player_interactions", "region_movement", "inner_voice", "fan_fiction"],
    conflicts: [],
    prerequisites: [],
    tags: ["pov", "multiplayer", "storytelling", "character-switch", "perspective"],
    complexity: 3,
    exampleConfig: { mode: "multi_pov", trigger: "both", cooldown: 0, memory_retention: "full", max_characters: 4, shared_inventory: false },
    stateVariables: ["active_pov", "pov_history", "pov_characters", "pov_cooldown_until"],
    genrePresets: {
      "Fantasy RPG": { mode: "hybrid", trigger: "narrator_driven", cooldown: 2, memory_retention: "partial", max_characters: 4, shared_inventory: false },
      "Detective Noir": { mode: "multi_pov", trigger: "player_request", cooldown: 0, memory_retention: "none", max_characters: 3, shared_inventory: false },
      "Ensemble Drama": { mode: "hybrid", trigger: "both", cooldown: 0, memory_retention: "full", max_characters: 6, shared_inventory: true },
    },
  },

  configSchema: [
    {
      key: "mode",
      label: "POV Mode",
      type: "select",
      options: [
        { value: "multiplayer", label: "Multiplayer", description: "Switch between real players' characters" },
        { value: "multi_pov", label: "Multi-POV", description: "Single player controls multiple characters" },
        { value: "hybrid", label: "Hybrid", description: "Both multiplayer hand-offs and multi-character control" },
      ],
      default: "multi_pov",
    },
    {
      key: "trigger",
      label: "Switch Trigger",
      type: "select",
      options: [
        { value: "player_request", label: "Player Only", description: "POV switches only on player request" },
        { value: "narrator_driven", label: "Narrator Driven", description: "AI can initiate POV switches for dramatic effect" },
        { value: "both", label: "Both", description: "Player or narrator can trigger switches" },
      ],
      default: "both",
    },
    {
      key: "cooldown",
      label: "Cooldown (turns)",
      type: "number",
      default: 0,
      description: "Minimum turns between POV switches (0 = no cooldown)",
    },
    {
      key: "memory_retention",
      label: "Memory Retention",
      type: "select",
      options: [
        { value: "full", label: "Full", description: "New POV character knows everything that happened" },
        { value: "partial", label: "Partial", description: "Knows major events but not private conversations or thoughts" },
        { value: "none", label: "None", description: "Only knows what they personally witnessed" },
      ],
      default: "full",
    },
    {
      key: "max_characters",
      label: "Max POV Characters",
      type: "number",
      default: 4,
      description: "Maximum number of playable POV characters (2-6)",
    },
    {
      key: "shared_inventory",
      label: "Shared Inventory",
      type: "boolean",
      default: false,
      description: "Whether POV characters share a single inventory or keep separate ones",
    },
    {
      key: "transition_default",
      label: "Default Transition Style",
      type: "select",
      options: [
        { value: "cut", label: "Hard Cut", description: "Immediate scene change" },
        { value: "flashback", label: "Flashback", description: "Transition via memory or flashback" },
        { value: "parallel", label: "Parallel", description: "Meanwhile… split-screen style" },
        { value: "dream", label: "Dream", description: "Surreal, dreamlike transition" },
      ],
      default: "cut",
    },
  ],

  systemPromptSection: (config) => {
    const mode = config.mode || "multi_pov";
    const trigger = config.trigger || "both";
    const cooldown = config.cooldown ?? 0;
    const memory = config.memory_retention || "full";
    const shared = config.shared_inventory === true;
    const transDefault = config.transition_default || "cut";

    const modeDesc: Record<string, string> = {
      multiplayer: "POV switches hand control to another real player's character.",
      multi_pov: "The player controls multiple characters, switching between them to experience the story from different angles.",
      hybrid: "Supports both multiplayer hand-offs and single-player multi-character control.",
    };

    const memoryDesc: Record<string, string> = {
      full: "The new POV character has full knowledge of all events — narrate as if they were aware of everything.",
      partial: "The new POV character knows major world events but NOT private conversations, inner thoughts, or secret actions of other characters. They may have heard rumors.",
      none: "The new POV character only knows what they personally witnessed or were told. They are unaware of scenes they weren't present for. Create dramatic irony.",
    };

    const triggerDesc: Record<string, string> = {
      player_request: "Only switch POV when the player explicitly requests it via the switch_pov tool.",
      narrator_driven: "You may initiate POV switches at dramatically appropriate moments — cliffhangers, parallel storylines, or when another character has a crucial scene. Announce the switch with a cinematic transition.",
      both: "The player can request POV switches, and you may also initiate them at dramatically powerful moments (cliffhangers, reveals, parallel action).",
    };

    return `## SWITCH POV (Rules Mechanic)
- Mode: ${mode} — ${modeDesc[mode]}
- Trigger: ${trigger} — ${triggerDesc[trigger]}
${cooldown > 0 ? `- Cooldown: ${cooldown} turns between switches. If a switch is requested too soon, narrate why the perspective can't shift yet.` : "- No cooldown on POV switches."}
- Memory retention: ${memory} — ${memoryDesc[memory]}
${shared ? "- SHARED INVENTORY: All POV characters share the same inventory and currency." : "- SEPARATE INVENTORY: Each POV character has their own inventory and currency. Items must be physically transferred between characters."}
- Default transition style: ${transDefault}

### POV Writing Rules
- Always write from the active POV character's perspective (third-person limited or first-person, matching narrator config).
- When switching, use the transition style to create a cinematic moment:
  - **cut**: End the current scene cleanly, then start the new perspective immediately.
  - **flashback**: "Hours earlier…" or "Meanwhile, in another part of the world…"
  - **parallel**: Split-screen narration showing both perspectives briefly before settling into the new one.
  - **dream**: A surreal, dreamlike transition blending both characters' experiences.
- After switching, briefly orient the reader: where is the new character, what are they doing, what's their emotional state?
- Use add_pov_character to register new playable characters when they are first introduced.
- Track active_pov, pov_history, and pov_characters in state variables.`;
  },

  tools: [
    {
      type: "function",
      function: {
        name: "switch_pov",
        description: "Switch the active point-of-view character. Updates the narrative perspective and tracks POV history.",
        parameters: {
          type: "object",
          properties: {
            target_character: { type: "string", description: "Name or ID of the character to switch to" },
            transition_style: { type: "string", enum: ["cut", "flashback", "parallel", "dream"], description: "How to narratively transition between perspectives" },
            reason: { type: "string", description: "Brief narrative reason for the switch (e.g. 'cliffhanger', 'parallel action', 'player request')" },
          },
          required: ["target_character"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "list_pov_characters",
        description: "List all available POV characters with their current status, location, and last active turn.",
        parameters: { type: "object", properties: {}, required: [] },
      },
    },
    {
      type: "function",
      function: {
        name: "add_pov_character",
        description: "Register a character as a playable POV character. Call when introducing a new character that the player can switch to.",
        parameters: {
          type: "object",
          properties: {
            name: { type: "string", description: "Character name" },
            id: { type: "string", description: "Unique character identifier" },
            location: { type: "string", description: "Current location" },
            status: { type: "string", description: "Character status (active, injured, etc.)" },
          },
          required: ["name"],
        },
      },
    },
  ],

  widgets: [
    {
      id: "pov_status",
      type: "status_bar_item",
      label: "POV",
      icon: "Eye",
      component: "POVStatusWidget",
      dataBindings: {
        activePov: "variables.active_pov",
        povCharacters: "variables.pov_characters",
        cooldownUntil: "variables.pov_cooldown_until",
      },
      visibility: "when_data",
      priority: 25,
      actions: [
        {
          id: "request_switch",
          label: "Switch POV",
          icon: "RefreshCw",
          messageTemplate: "[SWITCH_POV: {{target_character}}]",
        },
      ],
    },
    {
      id: "pov_panel",
      type: "sidebar_tab",
      label: "POV Characters",
      icon: "Users",
      component: "POVPanel",
      dataBindings: {
        activePov: "variables.active_pov",
        povCharacters: "variables.pov_characters",
        povHistory: "variables.pov_history",
        cooldownUntil: "variables.pov_cooldown_until",
      },
      visibility: "when_data",
      priority: 45,
      actions: [
        {
          id: "switch_to",
          label: "Switch To",
          icon: "ArrowRightLeft",
          messageTemplate: "[SWITCH_POV: {{target_character}}]",
        },
      ],
    },
  ],

  initializeState: () => ({
    active_pov: null,
    pov_history: [],
    pov_characters: [],
    pov_cooldown_until: 0,
  }),

  onEvent: (event) => {
    if (event.type === "quest_complete" || event.type === "combat_kill") {
      return {
        narrativeNotes: [
          `[POV SUGGESTION] A dramatic moment just occurred (${event.type}). Consider switching to another character's perspective.`,
        ],
      };
    }
    return null;
  },
};
